/**
 * App shell — persistent left rail (64px collapsed / 220px expanded, §13),
 * quiet topbar with the user menu, content column capped at 1200px.
 */

import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { useAuth } from "../lib/auth";
import { cx } from "../lib/format";

const RAIL_KEY = "va.rail_expanded";

function IconAgents({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" className={className} aria-hidden="true">
      <path
        d="M3 10h1M6 10V7m0 3v3m4-3V4m0 6v6m4-6V8m0 2v2m3-2h-1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconCalls({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" className={className} aria-hidden="true">
      <path
        d="M4.1 3.5h2.6l1.2 3.2-1.7 1.3a10.5 10.5 0 0 0 4.8 4.8l1.3-1.7 3.2 1.2v2.6c0 .7-.6 1.3-1.3 1.2A13.7 13.7 0 0 1 2.9 4.8c-.1-.7.5-1.3 1.2-1.3Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconNumbers({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" className={className} aria-hidden="true">
      <path
        d="M8 3 6.5 17M13.5 3 12 17M4 7.5h13M3 12.5h13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconSettings({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" className={className} aria-hidden="true">
      <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M10 2.8v2m0 10.4v2m7.2-7.2h-2M4.8 10h-2m11.3-5.1-1.4 1.4M7.3 12.7l-1.4 1.4m0-8.2 1.4 1.4m5.4 5.4 1.4 1.4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconCollapse({ className, flipped }: { className?: string; flipped?: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      className={cx(className, flipped && "rotate-180")}
      aria-hidden="true"
    >
      <path
        d="M11.5 6 7.5 10l4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const NAV_ITEMS = [
  { to: "/agents", label: "Agents", icon: IconAgents },
  { to: "/calls", label: "Calls", icon: IconCalls },
  { to: "/numbers", label: "Numbers", icon: IconNumbers },
  { to: "/settings", label: "Settings", icon: IconSettings },
] as const;

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(RAIL_KEY) !== "0";
    } catch {
      return true;
    }
  });

  function toggleRail() {
    setExpanded((prev) => {
      try {
        window.localStorage.setItem(RAIL_KEY, prev ? "0" : "1");
      } catch {
        // storage unavailable — session-only preference
      }
      return !prev;
    });
  }

  // User menu with click-outside + Escape dismissal.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    function onPointerDown(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  async function handleLogout() {
    setMenuOpen(false);
    try {
      await logout();
    } finally {
      navigate("/login");
    }
  }

  return (
    <div className="flex h-full min-h-screen bg-ink">
      {/* Left rail */}
      <aside
        className={cx(
          "flex shrink-0 flex-col border-r border-line bg-surface transition-[width]",
          expanded ? "w-rail-open" : "w-rail",
        )}
      >
        {/* Wordmark */}
        <NavLink
          to="/"
          className="flex h-14 items-center gap-2.5 border-b border-line px-[22px] text-bright"
        >
          <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5 shrink-0" aria-hidden="true">
            <path
              d="M2.5 10h1.2M6 10V6.8m0 3.2v3.2M10 10V3.5m0 6.5v6.5M14 10V7.5m0 2.5v2.5m3.5-2.5h-1.2"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
          {expanded && (
            <span className="whitespace-nowrap font-display text-sm font-medium tracking-display">
              Voice Agents
            </span>
          )}
        </NavLink>

        {/* Nav */}
        <nav className="flex flex-1 flex-col gap-1 p-2" aria-label="Primary">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              title={expanded ? undefined : label}
              className={({ isActive }) =>
                cx(
                  "flex h-9 items-center gap-3 rounded px-[14px] text-sm transition-colors",
                  isActive
                    ? "bg-surface-raised text-bright"
                    : "text-muted hover:bg-surface-raised hover:text-body",
                )
              }
            >
              <Icon className="h-5 w-5 shrink-0" />
              {expanded && <span className="whitespace-nowrap">{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Collapse toggle */}
        <button
          type="button"
          onClick={toggleRail}
          aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
          className="flex h-11 items-center gap-3 border-t border-line px-[14px] text-muted transition-colors hover:text-bright"
        >
          <IconCollapse className="h-5 w-5 shrink-0" flipped={!expanded} />
          {expanded && <span className="text-xs">Collapse</span>}
        </button>
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        <header className="flex h-14 shrink-0 items-center justify-end border-b border-line bg-ink px-6">
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              className="flex items-center gap-2 rounded border border-transparent px-2 py-1.5 text-sm text-body transition-colors hover:border-line hover:text-bright"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full border border-line bg-surface text-xs uppercase text-bright">
                {(user?.name?.[0] ?? user?.email?.[0] ?? "?").toUpperCase()}
              </span>
              <span className="hidden max-w-[180px] truncate sm:block">
                {user?.name || user?.email}
              </span>
              <svg viewBox="0 0 16 16" fill="none" className="h-3 w-3 text-muted" aria-hidden="true">
                <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full z-40 mt-1 w-56 rounded border border-line bg-surface-raised py-1"
              >
                <div className="border-b border-line px-3 py-2">
                  <p className="truncate text-sm text-bright">{user?.name || "—"}</p>
                  <p className="truncate font-mono text-xs text-muted">{user?.email}</p>
                </div>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/settings");
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-body transition-colors hover:bg-surface hover:text-bright"
                >
                  Settings
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="block w-full px-3 py-2 text-left text-sm text-body transition-colors hover:bg-surface hover:text-bright"
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Content */}
        <main className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-content px-6 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
